import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ChatCircleDots } from "@phosphor-icons/react";
import QuickReplies from "./chat/QuickReplies";
import ChatBody from "./chat/ChatBody";
import ChatHeader from "./chat/ChatHeader";
import ChatInput from "./chat/ChatInput";
import { useChatMessages } from "./chat/useChatMessages";

const QUICK_REPLIES = [
  "How does therapy work here?",
  "What's the pricing?",
  "How do I book a session?",
  "Tell me about Forward mentorship",
  "Do you offer a free consultation?",
];

/** Reorder chips so the most-clicked appears first, preserving the rest's order. */
function orderChipsByStats(chips, statsItems) {
  if (!Array.isArray(statsItems) || statsItems.length === 0) return chips;
  const counts = new Map(statsItems.map((s) => [s.chip, Number(s.count) || 0]));
  const top = [...chips].sort((a, b) => (counts.get(b) || 0) - (counts.get(a) || 0))[0];
  if (!top || (counts.get(top) || 0) === 0) return chips;
  return [top, ...chips.filter((c) => c !== top)];
}

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [orderedChips, setOrderedChips] = useState(QUICK_REPLIES);
  const scrollerRef = useRef(null);
  const { messages, busy, send, reset } = useChatMessages({ scrollerRef });

  // Public toggle from admin settings.
  useEffect(() => {
    api.get("/settings/public")
      .then((r) => setEnabled(r.data?.ai_chat_enabled !== false))
      .catch(() => {});
  }, []);

  // Refresh "Popular" chip ordering every time the panel opens.
  useEffect(() => {
    if (!open) return;
    api.get("/chat/chip-stats")
      .then((r) => setOrderedChips(orderChipsByStats(QUICK_REPLIES, r.data?.items || [])))
      .catch((e) => console.warn("[AIChat] chip-stats fetch failed:", e?.message || e));
  }, [open]);

  if (!enabled) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="md-ai-fab"
        aria-label="Open chat with MindDhara assistant"
        data-testid="ai-chat-fab"
      >
        <ChatCircleDots size={22} weight="duotone" />
        <span className="md-ai-fab-label">Ask MindDhara</span>
      </button>
    );
  }

  return (
    <div className="md-ai-panel" role="dialog" aria-label="MindDhara AI assistant" data-testid="ai-chat-panel">
      <ChatHeader onClose={() => setOpen(false)} />
      <ChatBody messages={messages} busy={busy} scrollerRef={scrollerRef} />
      {messages.length <= 1 && !busy && (
        <QuickReplies chips={orderedChips} onPick={send} />
      )}
      <ChatInput busy={busy} onSend={send} onReset={reset} />
    </div>
  );
}
